import {TokenService} from '@/services/TokenService';
import {createVectorTileContent, DisplayField, InfoWindowContent} from '@/lib/classes/InfoWindowContent';
import {mappedFields} from '@/lib/StandardFields';
import {gmAPI, MapService} from '@/services/MapService';
import {getStore} from '@/services/store';
import {wrapError} from '@northpoint-development/labs-common-core';
import {geohashForLocation} from 'geofire-common';
import {calculateSF, calculateTotalAcreage} from '../../PolyGraphic/PolygonGraphic';
import {buildAddress, convertEsriRingsToLatLng} from './esriUtils';

import type {Event, VectorTileGraphic} from '@/services/GraphicService/VectorTileGraphic/VectorTileGraphic';
import type {MapLayer} from '@/services/GraphicService/MapLayer';
import type {FieldConfig} from '@/lib/classes/InfoWindowContent/helpers/vectorTile';
import type {Comp} from '@/services/OverlaysService/Feature';
import type {Overlay} from '@/services/OverlaysService';

/**
 * This function is essentially our default feature click handler with a few modifications.
 * The tiles carry each feature's `id`, and querying the feature server by primary key is
 * significantly faster than querying by any other field, so it sends those ids rather than
 * building layer definitions for the query.
 *
 * This custom click handler might be a good reference for what we should update the default one to do in the future,
 * because it uses the layer's tokenName property and is more performant than layerDefs querying.
 */
const handleLightboxZoningBoundariesClick = async (event: Event, layer: MapLayer, graphic: VectorTileGraphic) => {
  // Step 1: Fetch the token and verify the server is working.
  const clickResults = event.features;
  if (!clickResults?.length) return;
  const featureServerUrl = layer.urlMap['featureServer'];
  if (!featureServerUrl) {
    console.warn('Missing feature server URL for vector layer.');
    return;
  }
  const formData = new URLSearchParams({f: 'json'});
  const headers: Record<string, string> = {'Content-Type': 'application/x-www-form-urlencoded'};
  const token = await TokenService.getToken(layer.tokenName);
  headers['Authorization'] = `Bearer ${token}`;
  const serverResponse = await fetch(`${featureServerUrl}?${formData.toString()}`, {method: 'GET', headers});
  if (!serverResponse.ok) {
    console.error(`Error fetching feature server data: ${serverResponse.statusText}`);
    return;
  }
  const serverJson: {layers: {name: string; id: number}[]} = await serverResponse.json();

  // Step 2: Organize clicked features by layer and feature id.
  const layerMap = new Map();
  clickResults.forEach((feature) => {
    const p = feature?.object?.properties as Record<string, string | number | undefined> | undefined;
    if (!p?.layerName || p.id === undefined) return;
    if (!layerMap.has(p.layerName)) {
      layerMap.set(p.layerName, {
        id: serverJson.layers.find((l) => l.name === p.layerName)?.id,
        ids: [],
      });
    }
    layerMap.get(p.layerName).ids.push(p.id);
  });

  // Step 3: Query the feature server for attributes of clicked features.
  const subjects: {
    feature: Record<string, string | number>;
    fields: {name: string; type: string; alias: string}[];
    layerName: string;
  }[] = [];
  for (const [layerName, data] of layerMap.entries()) {
    const query = new URLSearchParams({
      f: 'json',
      objectIds: data.ids.join(','),
      outFields: '*',
      returnGeometry: 'false',
    });
    // make a query per layer
    const queryResponse = await fetch(`${featureServerUrl}/${data.id}/query?${query.toString()}`, {method: 'GET', headers});
    if (!queryResponse.ok) {
      console.error(`Error fetching feature data: ${queryResponse.statusText}`);
      return;
    }
    const queryJson: {
      features: {attributes: Record<string, string | number>}[];
      fields: {name: string; type: string; alias: string}[];
    } = await queryResponse.json();
    queryJson.features.forEach((feature) => {
      subjects.push({
        feature: feature.attributes,
        fields: queryJson.fields,
        layerName,
      });
    });
  }

  // Step 4: Format the results for the info window.
  if (!subjects?.length) return;
  const currentSubLayers = layer.activeSubLayers();
  return subjects.map((subject) => ({
    content: createVectorTileContent(
      {
        banFields: layer.options[0].banFields ?? [],
        banValues: layer.options[0].banValues ?? [],
        idField: currentSubLayers[0].idField.toLowerCase(),
        name: layer.name,
        sublayerName: subject.layerName,
        titleFields: currentSubLayers[0].titleFields,
        fields: subject.fields as FieldConfig[],
        customFieldMapping: layer.options[0].customFieldMapping,
        schemaVersion: layer.schemaVersion,
        tableConfig: currentSubLayers[0].tableConfig,
      },
      {
        layerName: subject.layerName,
        feature: subject.feature,
      },
      graphic,
    ),
  }));
};

const _cachedParcels = new Map<string, google.maps.Polygon>();

/**
 * BMAPS-3265: Format contents for infowindow
 * Because we are querying an external feature server, we need some custom functionality for linking it
 * with our published vector tile layer.
 */
const handleLightboxParcelsClick = async (event: Event, layer: MapLayer, graphic: VectorTileGraphic) => {
  MapService.setCursor('wait');

  try {
    const clickResults = event.features;

    const token = await TokenService.getToken(layer.tokenName);
    const headers = {
      'Authorization': `Bearer ${token}`,
    };

    const infoWindowContents = await Promise.all(
      clickResults.map(async (clickResult) => {
        // step 1: get the parcel id and set up the query
        const id = (clickResult?.object?.properties as Record<string, string>)?.PARCEL_LID;
        if (!id) return null;

        const q = new URLSearchParams({
          f: 'json',
          where: `PARCEL_LID='${id}'`,
          outFields: '*',
          returnGeometry: 'true',
          token,
        });
        const featureServerUrl = layer.urlMap['featureServer'];

        // step 2: query the feature server for top level parcel info, create display fields
        // '3' is the Parcel/Assessment Info
        const paUrl = `${featureServerUrl}/3/query?`;
        const paResponse = await fetch(`${paUrl}${q.toString()}`, {method: 'GET', headers});
        const paJson = await paResponse.json();
        const feature = paJson.features?.[0];
        if (!feature) return null;

        const parcelDisplayFields = buildParcelDisplayFields(feature.attributes);

        // step 3: query the feature server for zoning info, create comps with display fields
        const comps = await fetchZoningComps(featureServerUrl, q.toString(), headers);

        const parcelGeometry = convertEsriRingsToLatLng(feature.geometry?.rings, paJson.spatialReference);

        // step 4: create the info window content
        return new InfoWindowContent({
          type: 'vectorTile',
          id,
          title: feature.attributes['OWNER_NAME'],
          metadata: {},
          displayFields: parcelDisplayFields,
          comps,
          host: {
            setHighlight: (state) => {
              let poly;
              if (_cachedParcels.has(id)) {
                poly = _cachedParcels.get(id);
              } else {
                poly = new gmAPI.Polygon({
                  paths: parcelGeometry,
                  fillColor: 'rgb(144,238,144)',
                  strokeColor: 'rgb(200,0,0)',
                });
                _cachedParcels.set(id, poly);
              }
              if (!poly) {
                console.error('Failed to create polygon for parcel highlight.');
                return;
              }
              if (state) {
                poly.setMap(MapService.getMap());
              } else {
                poly.setMap(null);
              }
            },
            addToOverlay: async (overlay: Overlay) => {
              try {
                let bounds = new gmAPI.LatLngBounds();
                // Assign each polygon in the parcel to a 'polys' object to store in Firestore.
                const polys: {[key: number]: {lat: number; lng: number}[]} = {};
                parcelGeometry.forEach((geo, i) => {
                  const path: {lat: number; lng: number}[] = [];
                  geo.forEach((point) => {
                    path.push({lat: point.lat(), lng: point.lng()});
                    bounds.extend(point);
                  });
                  polys[i] = path;
                });
                // Calculate geohash and normalize bounds to JSON.
                const center = bounds.getCenter();
                const geohash = geohashForLocation([center.lat(), center.lng()]);
                // Generate calculated fields
                const paths = Object.values(polys);
                let newDisplayFields = [
                  ...parcelDisplayFields,
                  new DisplayField({
                    unit: 'acreage',
                    allowEdit: false,
                    allowLabelEdit: false,
                    label: 'Acreage Calculated',
                    value: calculateTotalAcreage(paths),
                  }),
                  new DisplayField({
                    unit: 'squareFeet',
                    allowEdit: false,
                    allowLabelEdit: false,
                    label: 'Calculated SF',
                    value: calculateSF(paths),
                  }),
                ];
                const helper = (field: DisplayField) => field.value !== null && field.value !== undefined;
                newDisplayFields = newDisplayFields.filter(helper);
                // remove any comp display fields with a null/undefined value
                const newComps = comps;
                if (newComps) {
                  newComps.forEach((comp) => comp.displayFields = comp.displayFields.filter(helper));
                }
                // Generate feature data.
                const data = {
                  name: feature.attributes['OWNER_NAME'] ?? 'Parcel',
                  displayFields: newDisplayFields,
                  comps: newComps,
                  bounds: bounds.toJSON(),
                  geohash,
                  graphicData: {
                    featureType: 'polygon',
                    clickable: true,
                    bounds: bounds.toJSON(),
                    polys,
                  },
                };
                // Create the feature in the selected overlay.
                await overlay.newFeature(data);
              } catch (err) {
                throw wrapError(err, 'Error adding parcel to overlay');
              }
            },
          },
        });
      }),
    );

    return infoWindowContents.filter((content) => content !== null).map((content) => ({content}));
  } catch (error) {
    const newError = wrapError(error, 'Error in Lightbox Parcels click handler');
    getStore().dispatch('addErrorMessage', newError.message);
    return [];
  } finally {
    MapService.setCursor('default');
  }
};

/**
 * Fetches zoning comps from the Lightbox FeatureServer layer 6 (Zoning Info)
 * and builds a Comp[] array from the results.
 */
const fetchZoningComps = async (
  featureServerUrl: string,
  queryString: string,
  headers: Record<string, string>,
): Promise<Comp[]> => {
  const zoningUrl = `${featureServerUrl}/6/query?`;
  const zoningResponse = await fetch(`${zoningUrl}${queryString}`, {method: 'GET', headers});
  if (!zoningResponse.ok) return [];
  const zoningJson = await zoningResponse.json();

  const comps: Comp[] = [];
  zoningJson.features?.forEach((feature: {attributes: Record<string, string | number>}) => {
    const currentZoneDisplayFields: DisplayField[] = [];
    mappedFields['Lightbox Parcel Zoning'].forEach((field) => {
      const value = feature?.attributes?.[field.arcGisKey as string];
      currentZoneDisplayFields.push(new DisplayField({
        label: Array.isArray(field.label) ? field.label.join(', ') : field.label,
        unit: Array.isArray(field.formatUnit ?? field.unit) ? undefined : String(field.formatUnit ?? field.unit),
        allowEdit: false,
        allowLabelEdit: false,
        value,
      }));
    });
    comps.push({
      id: feature.attributes?.OBJECTID?.toString() || '',
      name: feature.attributes?.ZONING_CATEGORY?.toString() || 'Zoning Info',
      displayFields: currentZoneDisplayFields,
      type: 'Zoning',
      parent: {},
    });
  });
  return comps;
};

/**
 * Builds an array of DisplayFields from Lightbox Parcels feature attributes
 * using the mappedFields configuration.
 */
const buildParcelDisplayFields = (attributes: Record<string, string | number>): DisplayField[] => {
  const displayFields: DisplayField[] = [];
  mappedFields['Lightbox Parcels'].forEach((field) => {
    let value;
    if (field.id === 'lpAddress') {
      value = buildAddress(attributes, 'SITE_ADDR', 'SITE_CITY', 'SITE_STATE', 'SITE_ZIP');
    } else if (field.id === 'lpOwnerAddress') {
      value = buildAddress(attributes, 'MAIL_ADDR', 'MAIL_CITY', 'MAIL_STATE', 'MAIL_ZIP');
    } else if (field.id === 'lpMailAddress') {
      value = buildAddress(attributes, 'MAIL_ADDR', 'MAIL_CITY', 'MAIL_STATE', 'MAIL_ZIP');
    } else if (field.id === 'lpLandUseCode') {
      const prefix = attributes?.['USE_CODE_MUNI_DESC']?.toString().trim();
      const suffix = attributes?.['USE_CODE_MUNI']?.toString().trim();
      value = suffix ? `${prefix} (${suffix})` : prefix;
    } else {
      value = attributes?.[field.arcGisKey as string];
    }
    displayFields.push(new DisplayField({
      label: Array.isArray(field.label) ? field.label.join(', ') : field.label,
      unit: Array.isArray(field.formatUnit ?? field.unit) ? undefined : String(field.formatUnit ?? field.unit),
      allowEdit: false,
      allowLabelEdit: false,
      value,
    }));
  });
  return displayFields;
};


export {
  handleLightboxZoningBoundariesClick,
  handleLightboxParcelsClick,
  buildParcelDisplayFields,
  fetchZoningComps,
};
